import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { useMarketStore } from '@/stores/marketStore';
import { useFeedNeeds } from '@/hooks/useFeedNeeds';
import {
  SPORT_TABS,
  SPORT_LABEL,
  MULTI_OUTCOME_SPORTS,
  type GameRow,
  type MultiOutcomeEvent,
  type Sport,
} from '@/views/ValueDashboard/types';
import { pct, StatusPill } from '@/views/ValueDashboard/outcomeDisplay';
import { useAuthStore } from '@/stores/authStore';
import { useLiveGameAccess } from '@/hooks/useLiveGameAccess';
import { canAccessLiveGame, type Role } from '@/types/navigation';

type SportFilter = 'all' | Sport;

// Unified discovery row: a binary game (away/home) or an N-outcome event.
type Row =
  | { kind: 'game'; sport: Sport; key: string; gameState: GameRow['gameState']; startTimeMs: number | null; game: GameRow }
  | { kind: 'multi'; sport: Sport; key: string; gameState: MultiOutcomeEvent['gameState']; startTimeMs: number | null; event: MultiOutcomeEvent };

const STATE_RANK = { live: 0, pre: 1, final: 2 } as const;

/**
 * Live event discovery — a cross-sport, real-time browse of every game Kalshi
 * (via hydra) currently lists, sortable/filterable by sport. Binary games come
 * from getGames; 3-way+ events (soccer Home/Away/Draw) from
 * getMultiOutcomeEvents. Built entirely from data already in marketStore — it
 * opens NO new subscriptions, so it's safe against upstream IP rate limits.
 * Orderbook depth loads on demand only when a (binary) game is opened.
 */
export function LiveView() {
  // The live LIST spans every sport; `/live/*` is sport-scoped by the route
  // fallback (the game view declares its own), so this page declares 'all'.
  useFeedNeeds('live-list', { sports: 'all' });
  const version = useMarketStore((s) => s.version);
  const getGames = useMarketStore((s) => s.getGames);
  const getMultiOutcomeEvents = useMarketStore((s) => s.getMultiOutcomeEvents);
  const getActiveSports = useMarketStore((s) => s.getActiveSports);
  const [sportFilter, setSportFilter] = useState<SportFilter>('all');
  // Consumer feed filters (feed-filters-per-view): a cross-sport browse — the
  // sport chips themselves are derived from whatever is live, so this view
  // legitimately needs every sport. Moneyline rows only, which the defaults
  // already carry; nothing detail-only is opted in.
  useFeedNeeds('live', { sports: 'all' });

  const rowsAll = useMemo<Row[]>(() => {
    void version;
    // Only scan sports that actually have live markets — avoids a full
    // _markets pass per empty sport on every tick.
    const active = getActiveSports();
    const out: Row[] = [];
    for (const { id: sport } of SPORT_TABS) {
      if (!active.has(sport)) continue;
      if (MULTI_OUTCOME_SPORTS.has(sport)) {
        for (const e of getMultiOutcomeEvents(sport)) {
          out.push({ kind: 'multi', sport, key: e.key, gameState: e.gameState, startTimeMs: e.startTimeMs, event: e });
        }
      } else {
        for (const g of getGames(sport, 'moneyline', { statusFilter: 'active', dateFilter: 'all' })) {
          if (g.series || g.date.startsWith('9999')) continue;
          out.push({ kind: 'game', sport, key: g.key, gameState: g.gameState, startTimeMs: g.startTimeMs, game: g });
        }
      }
    }
    return out;
  }, [getGames, getMultiOutcomeEvents, getActiveSports, version]);

  const availableSports = useMemo<Sport[]>(() => {
    const seen = new Set<Sport>();
    for (const r of rowsAll) seen.add(r.sport);
    return SPORT_TABS.map((t) => t.id).filter((s) => seen.has(s));
  }, [rowsAll]);

  const rows = useMemo<Row[]>(() => {
    const filtered = sportFilter === 'all' ? rowsAll : rowsAll.filter((r) => r.sport === sportFilter);
    return [...filtered].sort((a, b) => {
      if (STATE_RANK[a.gameState] !== STATE_RANK[b.gameState]) return STATE_RANK[a.gameState] - STATE_RANK[b.gameState];
      return (a.startTimeMs ?? Infinity) - (b.startTimeMs ?? Infinity);
    });
  }, [rowsAll, sportFilter]);

  const liveCount = useMemo(() => rowsAll.filter((r) => r.gameState === 'live').length, [rowsAll]);

  return (
    <div className="flex h-full flex-col">
      <div className="border-border flex items-center justify-between border-b px-4 py-2">
        <div className="flex items-center gap-2">
          <Radio size={16} className="text-primary" />
          <span className="text-sm font-semibold">Live Event Discovery</span>
          <span className="text-muted-foreground font-mono text-xs">
            {liveCount} live · {rowsAll.length} events
          </span>
        </div>
      </div>

      <div className="border-border flex flex-wrap gap-1.5 border-b px-4 py-2">
        {(['all', ...availableSports] as SportFilter[]).map((sport) => (
          <button
            key={sport}
            type="button"
            onClick={() => setSportFilter(sport)}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-semibold uppercase transition-colors',
              sportFilter === sport
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            {sport === 'all' ? 'All' : SPORT_LABEL[sport]}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {rows.length === 0 ? (
          <div className="text-muted-foreground flex h-32 items-center justify-center text-sm">
            No live events right now
          </div>
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead className="bg-muted/50 text-muted-foreground sticky top-0 z-10 border-border border-b text-left text-[11px] uppercase">
              <tr>
                <th className="px-4 py-2 font-medium">Sport</th>
                <th className="px-3 py-2 font-medium">Match</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Start</th>
                <th className="px-3 py-2 text-right font-medium" colSpan={2}>Outcomes</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) =>
                r.kind === 'game' ? (
                  <GameRowView key={`g-${r.sport}-${r.key}`} g={r.game} sportLabel={SPORT_LABEL[r.sport]} />
                ) : (
                  <MultiRowView key={`m-${r.sport}-${r.key}`} e={r.event} sportLabel={SPORT_LABEL[r.sport]} />
                ),
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SportBadge({ label }: { label: string }) {
  return (
    <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px] font-bold uppercase">
      {label}
    </span>
  );
}

function GameRowView({ g, sportLabel }: { g: GameRow; sportLabel: string }) {
  const away = g.kalshi?.away?.priceCents ?? g.polymarket?.away?.priceCents ?? null;
  const home = g.kalshi?.home?.priceCents ?? g.polymarket?.home?.priceCents ?? null;
  // Single-game live trading entry — same predicate as the /live/:gameKey
  // route guard, so the link never renders for a trader the guard would eject.
  const role = (useAuthStore((s) => s.user?.role) ?? 'trader') as Role;
  const liveGameAccess = useLiveGameAccess();
  const canTrade = canAccessLiveGame(role, liveGameAccess) && g.kalshi != null;
  return (
    <tr className="border-border/50 hover:bg-muted/25 border-b">
      <td className="px-4 py-2"><SportBadge label={sportLabel ?? g.sport} /></td>
      <td className="px-3 py-2">
        <Link to={`/game/${g.date}/${g.awayCode}/${g.homeCode}`} className="hover:text-primary font-medium">
          {g.awayName || g.awayCode} <span className="text-muted-foreground">@</span> {g.homeName || g.homeCode}
        </Link>
      </td>
      <td className="px-3 py-2"><StatusPill state={g.gameState} /></td>
      <td className="text-muted-foreground px-3 py-2 font-mono text-xs">{g.date.slice(5)} {g.timePt}</td>
      <td className="px-3 py-2 text-right font-mono text-xs">{g.awayCode} {pct(away)}</td>
      <td className="px-3 py-2 text-right font-mono text-xs">{g.homeCode} {pct(home)}</td>
      <td className="px-3 py-2 text-right">
        {canTrade && (
          <Link
            to={`/live/${encodeURIComponent(g.key)}`}
            className="border-primary/50 text-primary hover:bg-primary/10 rounded border px-2 py-0.5 text-[11px] font-semibold uppercase"
            title="Open the single-game live trading view"
          >
            Trade
          </Link>
        )}
      </td>
    </tr>
  );
}

function MultiRowView({ e, sportLabel }: { e: MultiOutcomeEvent; sportLabel: string }) {
  return (
    <tr className="border-border/50 hover:bg-muted/25 border-b">
      <td className="px-4 py-2"><SportBadge label={sportLabel ?? e.sport} /></td>
      <td className="px-3 py-2 font-medium">{e.title || e.key}</td>
      <td className="px-3 py-2"><StatusPill state={e.gameState} /></td>
      <td className="text-muted-foreground px-3 py-2 font-mono text-xs">
        {(e.date ?? '').slice(5)} {e.timePt}
      </td>
      <td className="px-3 py-2 text-right" colSpan={2}>
        <div className="flex flex-wrap justify-end gap-1.5">
          {e.outcomes.map((o) => (
            <span key={o.ticker} className="bg-muted/60 rounded px-1.5 py-0.5 font-mono text-xs">
              {o.name}{' '}
              <span className="text-blue-400">K {pct(o.kalshi?.priceCents)}</span>{' '}
              <span className="text-purple-400">P {pct(o.polymarket?.priceCents)}</span>
            </span>
          ))}
        </div>
      </td>
      <td />
    </tr>
  );
}
